import type { GenerationView } from "@sonic-five-elements/contracts";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { api, mediaUrl } from "../lib/api";

const terminal = new Set(["READY_TEMPORARY", "READY_ARCHIVED", "FAILED", "CANCELLED", "SUBMISSION_UNKNOWN", "EXPIRED"]);

function failureMessage(status: GenerationView["status"], errorCode: string | null): string {
  if (status === "SUBMISSION_UNKNOWN") return "The provider did not confirm whether it received the request, so your credits were returned safely.";
  if (status === "CANCELLED") return "The music provider cancelled this task. Your credits were returned.";
  if (errorCode === "GENERATION_TIMEOUT") return "The music took longer than the delivery window. Your credits were returned.";
  if (errorCode === "ARCHIVE_SOURCE_EXPIRED") return "The temporary source expired before it could be archived. Your credits were returned.";
  return "The music could not be delivered. Your credits were returned automatically.";
}

export function ResultPage() {
  const { id = "" } = useParams();
  const [job, setJob] = useState<GenerationView | null>(null);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const { refresh } = useAuth();
  useEffect(() => {
    let timer: number | undefined;
    let active = true;
    let pollDelay = 5_000;
    const load = async () => {
      try {
        const next = await api.generation(id);
        if (!active) return;
        setJob(next);
        if (terminal.has(next.status)) void refresh();
        if (!terminal.has(next.status)) {
          timer = window.setTimeout(load, pollDelay);
          pollDelay = Math.min(10_000, Math.round(pollDelay * 1.4));
        }
      } catch (reason) { if (active) setError(reason instanceof Error ? reason.message : "Unable to load this generation."); }
    };
    void load();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [id, refresh]);
  if (error) return <section className="result-page"><h1>We could not open this creation.</h1><p className="form-error">{error}</p></section>;
  if (!job) return <div className="page-loading">Opening your listening room…</div>;
  const ready = job.status === "READY_TEMPORARY" || job.status === "READY_ARCHIVED";
  const handleDownload = async () => {
    if (!job.downloadUrl || downloading) return;
    setDownloadError("");
    setDownloading(true);
    try {
      await api.downloadMusic(job.downloadUrl, job.id);
      void api.track("music_downloaded", { jobId: job.id });
    } catch (reason) {
      setDownloadError(reason instanceof Error ? reason.message : "The MP3 download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };
  return <section className="result-page">
    <p className="eyebrow">YOUR FIVE-TONE CREATION</p><StatusBadge status={job.status} />
    <h1>{ready ? "Your music is ready" : "Your music is being created"}</h1>
    {!ready && !terminal.has(job.status) && <div className="generating-visual"><span /><span /><span /><span /><span /></div>}
    {ready && <div className="player-card"><div className="record-art"><span>五</span></div><div className="player-content"><p>AI-Generated {job.mode === "song" ? "Song with Vocals" : "Instrumental Music"}</p><h2>{job.title || `${job.tone.toUpperCase()} · ${job.mood}`}</h2><audio controls preload="metadata" crossOrigin="use-credentials" src={mediaUrl(job.playUrl)} onPlay={() => void api.track(job.status === "READY_TEMPORARY" ? "temporary_audio_played" : "music_played", { jobId: job.id })} />{job.expiresAt && <p className="expiry-note">Available until {new Date(job.expiresAt).toLocaleString()}. Download before it expires.</p>}{downloadError && <p className="form-error">{downloadError}</p>}<button className="button secondary" type="button" disabled={downloading} onClick={() => void handleDownload()}>{downloading ? "Downloading…" : "Download MP3"}</button></div></div>}
    {["FAILED", "CANCELLED", "SUBMISSION_UNKNOWN"].includes(job.status) && <p>{failureMessage(job.status, job.errorCode)} Please try another creation.</p>}
    {job.status === "EXPIRED" && <p>This music has reached the end of its availability period.</p>}
    <div className="result-actions"><Link className="button ghost" to="/create">Create another</Link><Link className="text-link" to="/library">View My Music</Link></div>
  </section>;
}
